import { useMemo } from "react";

// eslint-disable-next-line @nx/enforce-module-boundaries
import { AccessesKeyEnum } from "@saas/account/utils/@enum/subaccount/accesses-key/accesses-key.enum";
import { env } from "@saas/config/shared/env/env";
import { FlagKeys } from "@saas/flags/utils/@type/flag-keys/flag-keys";
// eslint-disable-next-line @nx/enforce-module-boundaries
import { NavigationInterface } from "@saas/layout/utils/@interface/navigation/navigation.interface";
import {
  AnalyticsIcon,
  ConsignmentIcon,
  HelpIcon,
  InventoryIcon,
  MarketplaceIcon,
  OrdersIcon,
  PromotionIcon,
  SalesIcon,
  SalesTeamIcon,
  SettingsIcon,
} from "@saas/shared/icon";
import { NewMenuEnum } from "@saas/shared/utils/@enum/new-menu/new-menu.enum";

export type UseNavigation = {
  navigation: ReadonlyArray<NavigationInterface>;
};

type UseNavigationParams = {
  defaultFlags: Record<FlagKeys, boolean>;

  // New parameter for organization function
  isHeadquarter: boolean;
};

export const useNavigation = ({
  defaultFlags,
  isHeadquarter,
}: UseNavigationParams): UseNavigation => {
  const navigation = useMemo<ReadonlyArray<NavigationInterface>>(() => {
    const {
      "consignment": consignmentFlag,
      "consignment/approvals": consignmentApprovalFlag,
      "consignment/customer-stock": customerStockFlag,
      "consignment/deliveries": consignmentDeliveriesFlag,
      "consignment/invoices": consignmentInvoicesFlag,
      "inventory": inventoryFlag,
      "settings": settingsFlag,
      "settings/approvals": settingsApprovalFlag,
      "settings/bank-account": bankAccountFlag,
      "store/announcement": storeAnnouncementFlag,
    } = defaultFlags;

    if (isHeadquarter) {
      return [
        {
          label: "Tim Sales",
          href: "/salesperson",
          testid: "home__menu__store-salesperson",
          enabled: true,
          icon: SalesTeamIcon,
          accesses: [AccessesKeyEnum.SALESPERSON_ALL],
        },
        {
          label: "Sales App",
          href: env.SALES_URL,
          target: "_blank",
          icon: SalesIcon,
          testid: "home__menu__sales-app",
          enabled: settingsApprovalFlag,
        },
      ];
    }

    return [
      {
        label: "Toko Saya",
        href: "#",
        icon: MarketplaceIcon,
        testid: "home__menu__store",
        enabled: true,
        accesses: [
          AccessesKeyEnum.POS_ALL,
          AccessesKeyEnum.CATALOG_ALL,
          AccessesKeyEnum.SALESPERSON_ALL,
        ],
        children: [
          {
            label: "Tim Sales",
            href: "/salesperson",
            testid: "home__menu__store-salesperson",
            enabled: true,
            accesses: [AccessesKeyEnum.SALESPERSON_ALL],
          },
          {
            label: "Kasir",
            href: env.POS_URL,
            target: "_blank",
            testid: "home__menu__order-cashier",
            enabled: true,
            accesses: [AccessesKeyEnum.POS_ALL],
          },
          {
            label: "Katalog Melaka",
            href: "/catalog",
            testid: "home__menu__store-catalog",
            enabled: true,
            accesses: [AccessesKeyEnum.CATALOG_ALL],
          },
          {
            label: "Pengumuman",
            href: "/store/announcement",
            testid: "home__menu__store-announcement",
            enabled: storeAnnouncementFlag,
          },
        ],
      },
      {
        label: "Order Saya",
        href: "/order",
        icon: OrdersIcon,
        testid: "home__menu__order",
        enabled: true,
        accesses: [AccessesKeyEnum.ORDER_ACTION, AccessesKeyEnum.ORDER_VIEW],
      },
      {
        label: "Konsinyasi",
        href: "#",
        icon: ConsignmentIcon,
        testid: "home__menu__consignment",
        enabled: consignmentFlag,
        accesses: [
          AccessesKeyEnum.CONSIGNMENT_DELIVERY_VIEW,
          AccessesKeyEnum.CONSIGNMENT_DELIVERY_ACTION,
          AccessesKeyEnum.CUSTOMER_STOCK_VIEW,
          AccessesKeyEnum.CUSTOMER_STOCK_ACTION,
          AccessesKeyEnum.INVOICE_DATA_VIEW,
        ],
        children: [
          {
            label: "Daftar Konsinyasi",
            href: "/consignment/deliveries",
            enabled: consignmentDeliveriesFlag,
            testid: "home__submenu__consignment_deliveries",
            accesses: [
              AccessesKeyEnum.CONSIGNMENT_DELIVERY_VIEW,
              AccessesKeyEnum.CONSIGNMENT_DELIVERY_ACTION,
            ],
          },
          {
            label: "Stok Pelanggan",
            href: "/consignment/customer-stocks",
            enabled: customerStockFlag,
            testid: "home__submenu__consignment_customer_stocks",
            accesses: [
              AccessesKeyEnum.CUSTOMER_STOCK_VIEW,
              AccessesKeyEnum.CUSTOMER_STOCK_ACTION,
            ],
          },
          {
            label: "Data Invoice",
            href: "/consignment/invoices",
            enabled: consignmentInvoicesFlag,
            testid: "home__submenu__consignment_invoices",
            accesses: [AccessesKeyEnum.INVOICE_DATA_VIEW],
          },
        ],
      },
      {
        label: "Sales App",
        href: env.SALES_URL,
        target: "_blank",
        icon: SalesIcon,
        testid: "home__menu__sales-app",
        enabled: settingsApprovalFlag,
      },
      {
        label: "Produk & Stok",
        href: "#",
        icon: InventoryIcon,
        testid: "home__menu__inventory",
        enabled: inventoryFlag,
        accesses: [
          AccessesKeyEnum.PRODUCT_ACTION,
          AccessesKeyEnum.PRODUCT_VIEW,
          AccessesKeyEnum.STOCK_ACTION,
          AccessesKeyEnum.STOCK_VIEW,
          AccessesKeyEnum.VIRTUAL_BUNDLING_ACTION,
          AccessesKeyEnum.VIRTUAL_BUNDLING_VIEW,
          AccessesKeyEnum.PRODUCT_COLLECTION_ACTION,
          AccessesKeyEnum.PRODUCT_COLLECTION_VIEW,
        ],
        children: [
          {
            label: "Produk",
            href: "/inventory/products",
            enabled: true,
            testid: "home__submenu__inventory_product_list",
            accesses: [
              AccessesKeyEnum.PRODUCT_ACTION,
              AccessesKeyEnum.PRODUCT_VIEW,
            ],
          },
          {
            label: "Stok Inventori",
            href: "/inventory/stocks",
            enabled: true,
            testid: "home__submenu__inventory_stocks",
            accesses: [
              AccessesKeyEnum.STOCK_ACTION,
              AccessesKeyEnum.STOCK_VIEW,
            ],
          },
          {
            label: "Virtual Bundling",
            href: "/inventory/bundling",
            enabled: true,
            testid: "home__submenu__inventory_virtual_bundling",
            accesses: [
              AccessesKeyEnum.VIRTUAL_BUNDLING_ACTION,
              AccessesKeyEnum.VIRTUAL_BUNDLING_VIEW,
            ],
          },
          {
            label: "Koleksi Produk",
            href: "/inventory/collections",
            enabled: true,
            isNew: true,
            keyIndicator: NewMenuEnum.PRODUCT_COLLECTIONS,
            accesses: [
              AccessesKeyEnum.PRODUCT_COLLECTION_ACTION,
              AccessesKeyEnum.PRODUCT_COLLECTION_VIEW,
            ],
            testid: "home__submenu__inventory_product_collections",
          },
        ],
      },
      {
        label: "Analisis Bisnis",
        href: "#",
        icon: AnalyticsIcon,
        testid: "home__menu__analysis",
        enabled: true,
        accesses: [
          AccessesKeyEnum.REPORT_LIST_ALL,
          AccessesKeyEnum.PRICE_MONITORING_ALL,
          AccessesKeyEnum.SHOP_MONITORING_ALL,
        ],
        children: [
          {
            label: "Laporan Toko",
            href: "/reports/list",
            enabled: true,
            testid: "home__submenu__reports_list",
            accesses: [AccessesKeyEnum.REPORT_LIST_ALL],
          },
        ],
      },
      {
        label: "Promosi Saya",
        href: "#",
        icon: PromotionIcon,
        testid: "home__menu__promotion",
        enabled: true,
        accesses: [
          AccessesKeyEnum.PROMOTION_ALL,
          AccessesKeyEnum.VOUCHER_VIEW,
          AccessesKeyEnum.VOUCHER_ACTION,
        ],
        children: [
          {
            label: "Promosi Melaka",
            href: "/promotion/store",
            enabled: true,
            testid: "home__menu__promotion-melaka",
            accesses: [AccessesKeyEnum.PROMOTION_ALL],
          },
          {
            label: "Voucher",
            href: "/voucher",
            enabled: true,
            testid: "home__submenu__promotion-voucher",
            accesses: [
              AccessesKeyEnum.VOUCHER_VIEW,
              AccessesKeyEnum.VOUCHER_ACTION,
            ],
          },
        ],
      },
      {
        label: "Pengaturan",
        href: "#",
        testid: "home__menu__settings",
        icon: SettingsIcon,
        enabled: settingsFlag,
        accesses: [
          AccessesKeyEnum.SETTINGS_ORDER_APPROVAL_ALL,
          AccessesKeyEnum.BANK_ACCOUNT_INFORMATION_ALL,
        ],
        children: [
          {
            label: consignmentApprovalFlag
              ? "Persetujuan"
              : "Persetujuan Pesanan",
            enabled: settingsApprovalFlag,
            href: "/settings/approvals",
            testid: "home__submenu__settings-approvals",
            accesses: [AccessesKeyEnum.SETTINGS_ORDER_APPROVAL_ALL],
          },
          {
            label: "Akun Bank",
            href: "/settings/bank-account",
            testid: "home__submenu__settings-bank-account",
            enabled: bankAccountFlag,
            accesses: [AccessesKeyEnum.BANK_ACCOUNT_INFORMATION_ALL],
          },
        ],
      },
      {
        label: "Pusat Bantuan",
        href: env.HELP_URL,
        icon: HelpIcon,
        testid: "home__menu__help-center",
        enabled: true,
        sticky: true,
        className: "mb-0",
      },
    ];
  }, [defaultFlags, isHeadquarter]);

  return { navigation: navigation };
};

export default useNavigation;
